import React, { useState, useEffect, useRef } from 'react';
import { getTheme } from '../theme';
import { API, authHeaders } from '../utils/helpers';
import { laborPriceSuggestion } from '../utils/laborPrice';

// ── Adding and editing a ticket's line items ─────────────────────────────────
//
// Lifted out of TicketDetail on 2 Oct, unchanged, so the bench screen adds and
// edits parts, services, labor and misc lines with the SAME code as the
// Service Queue's ticket rather than a second copy that drifts. Everything
// here was in TicketDetail and its comments travelled with it.
//
// The caller owns the ticket (detail/setDetail) and how to reload it. This
// hook owns the add panel, the line editor modal, and the per-line actions,
// and hands them back for the caller to place:
//
//   addItemPanel    the search bar plus Services / Labor / Misc panels
//   lineEditorModal the edit-a-line popover (null when closed)
//   updateItemQuantity, deleteLineItem, openLineEditor   per-line actions
//   lineStock, totalsPending, employees, discounts       data the list shows
//
// reload() must return a promise of the fresh ticket. totalsPending is set by
// an optimistic append and cleared when the reload after it lands.
// Quotes (23 Oct) use this same editor: `writeBase` points the WRITES (add,
// edit, remove) at /api/quotes/<id>, whose routes take the same bodies and
// return the same line shapes as the ticket routes. Searches and stock are
// reads that do not depend on a ticket, so they keep the ticket path (with 0
// when there is no ticket). `noun` names the thing in prompts; `handParts`
// adds the "+ Order part" panel for a part no catalog lists.
export function useLineItemEditor({ ticketId, detail, setDetail, reload, theme = 'dark', writeBase = null, noun = 'ticket', handParts = false }) {
  const readBase = `${API}/api/tickets/${ticketId || 0}`;
  writeBase = writeBase || `${API}/api/tickets/${ticketId}`;
  const isDark = theme === 'dark';
  const T = getTheme(theme);

  // Add Item — search bar is always live; Labor and Misc panels open on demand.
  const [activePanel, setActivePanel] = useState(null); // null | 'labor' | 'misc' | 'services'

  const [partsQuery, setPartsQuery] = useState('');
  const [partsResults, setPartsResults] = useState([]);
  const [partsSearching, setPartsSearching] = useState(false);
  const [partsQty, setPartsQty] = useState({});
  const [partsBusy, setPartsBusy] = useState(false);
  const [partsToast, setPartsToast] = useState('');
  const [partsToastBad, setPartsToastBad] = useState(false); // red instead of green

  // Vendor catalogs (22 Oct): Lightspeed's catalog search, opt-in per device
  // because each search takes several seconds. Runs on Enter only.
  const [includeCatalog, setIncludeCatalog] = useState(() => {
    try { return localStorage.getItem('sb-include-catalog') === '1'; } catch (_) { return false; }
  });
  const [catalog, setCatalog] = useState(null); // { results, other, narrowed } | null
  const [catalogBusy, setCatalogBusy] = useState(false);
  const [catalogError, setCatalogError] = useState('');
  const [showOtherVendors, setShowOtherVendors] = useState(false);
  const toggleCatalog = (on) => {
    setIncludeCatalog(on);
    try { localStorage.setItem('sb-include-catalog', on ? '1' : '0'); } catch (_) { /* per-device convenience only */ }
    if (!on) setCatalog(null);
  };
  const [catalogQuery, setCatalogQuery] = useState(''); // the search the results came from
  const [catalogQty, setCatalogQty] = useState({});
  // A part whose catalog has no MSRP, or is over six months old, is priced by
  // the tech before it becomes an item (Manny, 22 Oct): another vendor's MSRP
  // for the same part, keystone, a margin, or a typed price.
  const catalogNeedsPricing = (r) => !(r.msrp > 0) || (Date.now() - Date.parse(r.catalogUpdated || '') > 180 * 864e5);
  const [pricing, setPricing] = useState(null); // { id, loading, error, opts, choice, margin, manual, cost }
  const openPricing = async (r) => {
    let margin = '';
    try { margin = localStorage.getItem('sb-catalog-margin') || ''; } catch (_) { /* per-device convenience */ }
    setPricing({ id: r.catalogItemID, loading: true, error: '', opts: null, choice: null, margin, manual: '', cost: r.cost > 0 ? String(r.cost) : '' });
    try {
      const res = await fetch(`${readBase}/catalog-price-options?q=${encodeURIComponent(catalogQuery)}&catalogItemID=${encodeURIComponent(r.catalogItemID)}`, { credentials:'include', headers: authHeaders() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Could not read the price options.');
      setPricing(p => p && p.id === r.catalogItemID ? { ...p, loading: false, opts: d, choice: d.elsewhere && d.elsewhere.length ? 'elsewhere-0' : null } : p);
    } catch (e) {
      setPricing(p => p && p.id === r.catalogItemID ? { ...p, loading: false, error: e.message } : p);
    }
  };
  // The price the chosen option comes to, or null.
  const pricedAt = (p) => {
    if (!p || !p.choice) return null;
    const cost = Number(p.cost) || 0;
    if (p.choice.startsWith('elsewhere-')) { const e = p.opts && p.opts.elsewhere[Number(p.choice.slice(10))]; return e ? e.msrp : null; }
    if (p.choice === 'catalog') return p.opts && p.opts.msrp > 0 ? p.opts.msrp : null;
    if (p.choice === 'keystone') return cost > 0 ? Math.round(cost * 200) / 100 : null;
    if (p.choice === 'margin') { const m = Number(p.margin); return cost > 0 && m > 0 && m < 100 ? Math.round((cost / (1 - m / 100)) * 100) / 100 : null; }
    if (p.choice === 'manual') { const v = Number(p.manual); return v > 0 ? Math.round(v * 100) / 100 : null; }
    return null;
  };
  const addCatalogPart = async (r, chosen = null) => {
    if (!chosen && catalogNeedsPricing(r)) { openPricing(r); return; }
    const qty = catalogQty[r.catalogItemID] || 1;
    setPartsBusy(true);
    try {
      const res = await fetch(`${writeBase}/add-catalog-item`, {
        method:'POST', credentials:'include', headers: authHeaders(true),
        body: JSON.stringify({ q: catalogQuery, catalogItemID: r.catalogItemID, quantity: qty, ...(chosen || {}) }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Add failed (${res.status})`);
      appendLine(body.line);
      setPricing(null);
      setPartsQuery(''); setPartsResults([]); setCatalog(null);
      setPartsToastBad(false);
      setPartsToast(body.quoted
        ? `Added to the ${noun}, marked to order. It becomes an item in Lightspeed only if the quote is approved.`
        : body.created ? `Added. ${r.description} is now an item in Lightspeed too.` : 'Added. You already had this part as an item, so that one was used.');
      setTimeout(() => setPartsToast(''), 5000);
      setPartsBusy(false);
      reconcileAfterAdd();
      return;
    } catch (e) {
      setPartsToastBad(true);
      setPartsToast(`Could not add ${r.description}. ${e.message}`);
      setTimeout(() => setPartsToast(''), 6000);
    }
    setPartsBusy(false);
  };
  // Each search gets a number; an answer that arrives after a newer search,
  // or after the part was found in inventory and added, is dropped.
  const catalogSeq = useRef(0);
  // Seconds since the catalog search started, for the progress line: a wait
  // of several seconds needs to look alive (Manny, 22 Oct).
  const [catalogElapsed, setCatalogElapsed] = useState(0);
  useEffect(() => {
    if (!catalogBusy) { setCatalogElapsed(0); return undefined; }
    const started = Date.now();
    const id = setInterval(() => setCatalogElapsed(Math.floor((Date.now() - started) / 1000)), 500);
    return () => clearInterval(id);
  }, [catalogBusy]);
  const dropCatalog = () => { catalogSeq.current += 1; setCatalog(null); setCatalogBusy(false); };
  const runCatalogSearch = async (q) => {
    const mine = ++catalogSeq.current;
    setCatalogQuery(q);
    setCatalogBusy(true); setCatalogError(''); setCatalog(null); setShowOtherVendors(false);
    try {
      const r = await fetch(`${readBase}/catalog-search?q=${encodeURIComponent(q)}`, { credentials:'include', headers: authHeaders() });
      const d = await r.json().catch(() => ({}));
      if (mine !== catalogSeq.current) return;
      if (!r.ok) throw new Error(d.error || 'The catalog search did not answer.');
      setCatalog(d);
    } catch (e) { if (mine === catalogSeq.current) setCatalogError(e.message); }
    if (mine === catalogSeq.current) setCatalogBusy(false);
  };

  // "+ Order part" (quotes only): a part no catalog lists, typed by hand and
  // marked to order. Created in Lightspeed only when the quote is approved.
  const [hand, setHand] = useState({ description: '', vendor: '', vendorPart: '', unitCost: '', unitPrice: '', quantity: 1, tax: true });
  const [handBusy, setHandBusy] = useState(false);
  const [handError, setHandError] = useState('');
  async function submitHand() {
    setHandBusy(true); setHandError('');
    try {
      const r = await fetch(`${writeBase}/add-hand`, {
        method:'POST', credentials:'include', headers: authHeaders(true),
        body: JSON.stringify({ ...hand, unitPrice: parseFloat(hand.unitPrice), unitCost: hand.unitCost === '' ? null : parseFloat(hand.unitCost), quantity: parseInt(hand.quantity, 10) || 1 }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || 'Add failed');
      appendLine(body.line);
      reconcileAfterAdd();
      setHand({ description: '', vendor: '', vendorPart: '', unitCost: '', unitPrice: '', quantity: 1, tax: true });
      setActivePanel(null);
    } catch (e) { setHandError(e.message); }
    setHandBusy(false);
  }

  const [laborDescription, setLaborDescription] = useState('');
  const [laborHours, setLaborHours] = useState(0);
  const [laborMinutes, setLaborMinutes] = useState(30);
  const [laborPrice, setLaborPrice] = useState('');
  const [laborBusy, setLaborBusy] = useState(false);
  const [laborRate, setLaborRate] = useState(null); // $/hour, from Settings — null until fetched or if shop hasn't set one
  // Separate from laborRate: null means BOTH "not fetched yet" and "not set",
  // and the no-rate hint must only show for the second.
  const [laborRateLoaded, setLaborRateLoaded] = useState(false);
  const [laborPriceManual, setLaborPriceManual] = useState(false); // tech typed a price directly — stop auto-calculating
  // Discount / warranty / tax at CREATION. These were editable on an existing
  // line but not settable on a new one, so every taxed or discounted line had to
  // be added and then immediately reopened.
  //
  // Tax defaults to CHECKED. Shop rule: labor, inventory and misc are all
  // taxable, and an untaxed line is the exception rather than the default. This
  // deliberately changes what an untouched line does — a misc charge used to be
  // created untaxed simply because nobody was asked.
  const [laborDiscountID, setLaborDiscountID] = useState('');
  const [laborWarranty, setLaborWarranty] = useState(false);
  const [laborTax, setLaborTax] = useState(true);

  const [miscDescription, setMiscDescription] = useState('');
  const [miscQty, setMiscQty] = useState(1);
  const [miscDiscountID, setMiscDiscountID] = useState('');
  const [miscWarranty, setMiscWarranty] = useState(false);
  const [miscTax, setMiscTax] = useState(true);
  const [miscPrice, setMiscPrice] = useState('');
  const [miscBusy, setMiscBusy] = useState(false);

  // Service picker. Items come from the shop's own Lightspeed catalog and are
  // grouped by the categories the shop already maintains there — no Spinboard-
  // side configuration to set up or keep in sync.
  const [services, setServices] = useState(null); // null = not loaded yet
  const [servicesBusy, setServicesBusy] = useState(false);
  const [servicesError, setServicesError] = useState(false);
  const [serviceCategory, setServiceCategory] = useState(null); // null = chips only, nothing expanded
  const [addingServiceId, setAddingServiceId] = useState(null);

  // Line editor (Labor / Misc). Modal popover.
  const [editingLine, setEditingLine] = useState(null); // full item from lineItems, or null
  const [editForm, setEditForm] = useState({ description:'', qty:1, unitPrice:'', hours:0, minutes:0 });
  const [editBusy, setEditBusy] = useState(false);
  // Set once the tech types into the modal's Price field — from then on
  // hours/minutes stop overwriting their number, mirroring laborPriceManual
  // on the Add Labor form.
  const [editPriceManual, setEditPriceManual] = useState(false);
  const [totalsPending, setTotalsPending] = useState(false);

  // A quote that has been sent is read-only the same way (detail.checkedOut).
  const checkedOut = !!(detail?.saleID && detail.saleID !== '0') || detail?.checkedOut === true;

  // Reload, and settle totals an optimistic append left pending.
  const loadTicketDetail = () => reload().then(d => { setTotalsPending(false); return d; });

  // Optimistic append. The add routes return the line they just wrote, shaped
  // exactly as getTicketDetail shapes it, so it can go straight into the list
  // without waiting on a reload that fans out across several Lightspeed calls.
  //
  // Labor and Parts subtotals are summed from lineItems in TicketCard, so those
  // update in the same tick. Tax and the ticket total come from the server and
  // cannot be derived here without a second implementation of the money math —
  // which is exactly the duplication that undercharged a customer in July. So
  // they are marked pending instead, and the background reload settles them.
  const appendLine = (line) => {
    if (!line) return;
    setTotalsPending(true);
    setDetail(prev => prev
      ? { ...prev, lineItems: [...(prev.lineItems || []), line] }
      : prev);
  };

  // Reconcile after an optimistic append. Never awaited by the caller: the
  // point is that the tech is not blocked on it. A failure leaves the appended
  // line in place — it is really on the ticket, the write succeeded — and the
  // totals stay pending rather than showing a number we could not confirm.
  const reconcileAfterAdd = () => {
    loadTicketDetail().catch(err => console.error('Reload after add failed:', err));
  };

  // A different ticket: clear everything that belonged to the last one, or a
  // pending reconcile leaves the next ticket's totals faded.
  useEffect(() => {
    setTotalsPending(false);
    setActivePanel(null);
    setPartsQuery('');
    setPartsResults([]);
    setEditingLine(null);
  }, [ticketId]);

  useEffect(() => {
    fetch(`${API}/api/settings`, { credentials:'include', headers: authHeaders() })
      .then(r => r.json())
      .then(s => { setLaborRate(s.laborRate ? parseFloat(s.laborRate) : null); setLaborRateLoaded(true); })
      .catch(() => setLaborRate(null));
  }, []);

  // Auto-calculate price from hours/minutes × shop labor rate — stays live
  // until the tech types directly into the price field, at which point we
  // stop overwriting their number.
  useEffect(() => {
    if (laborPriceManual || !laborRate) return;
    const totalHours = (parseFloat(laborHours) || 0) + (parseFloat(laborMinutes) || 0) / 60;
    setLaborPrice(totalHours > 0 ? (totalHours * laborRate).toFixed(2) : '');
  }, [laborHours, laborMinutes, laborRate, laborPriceManual]);

  useEffect(() => {
    // Reset spinner too — if the effect re-runs before the last fetch fired
    // (e.g. clear button, opening Labor/Misc), the previous "Searching…" was
    // never cleared by any finally() and would sit there indefinitely.
    dropCatalog(); setCatalogError('');
    if (partsQuery.trim().length < 2) { setPartsResults([]); setPartsSearching(false); return; }
    setPartsSearching(true);
    const handle = setTimeout(() => {
      fetch(`${readBase}/item-search?q=${encodeURIComponent(partsQuery.trim())}`, { credentials:'include', headers: authHeaders() })
        .then(r => r.json())
        .then(data => {
          const results = data.results || [];
          setPartsResults(results);
          // Stock is fetched separately rather than inline in the search
          // response: every lookup is a throttled Lightspeed call, so blocking
          // results on 15 of them would make search unusable. Results render
          // immediately and availability fills in behind them — instantly for
          // anything already cached, which in practice is most repeat searches.
          // itemType, NOT isLabor, and deliberately so: stock is about whether
          // Lightspeed tracks quantity for this item, which is what
          // non_inventory means. Tax class decides the write path; item type
          // decides whether there is a number to show.
          const ids = results.filter(r => r.itemType !== 'non_inventory').map(r => r.itemID);
          if (!ids.length) return;
          fetch(`${readBase}/stock?itemIDs=${ids.join(',')}`, { credentials:'include', headers: authHeaders() })
            .then(r => r.json())
            .then(d => {
              const stock = d.stock || {};
              // Merge by ID rather than replacing wholesale — the tech may
              // have typed on and moved to different results by now.
              setPartsResults(prev => prev.map(r => stock[String(r.itemID)]
                ? { ...r, stock: stock[String(r.itemID)] }
                : r));
            })
            .catch(() => {});
        })
        .catch(() => setPartsResults([]))
        .finally(() => setPartsSearching(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [partsQuery, ticketId]);

  // ── Panel close / toggle helpers ───────────────────────────────────────
  const clearPartsSearch = () => {
    setPartsQuery('');
    setPartsResults([]);
    dropCatalog(); setCatalogError('');
  };

  const closeActivePanel = () => {
    setLaborDescription(''); setLaborHours(0); setLaborMinutes(30); setLaborPrice(''); setLaborPriceManual(false);
    setMiscDescription(''); setMiscQty(1); setMiscPrice('');
    setActivePanel(null);
  };

  const toggleActivePanel = (which) => {
    if (activePanel === which) { closeActivePanel(); return; }
    // Clear the OTHER panels' state before opening this one. Written as explicit
    // per-panel guards rather than an if/else — the two-way version silently did
    // the wrong thing the moment a third panel existed.
    if (which !== 'misc')  { setMiscDescription(''); setMiscQty(1); setMiscPrice(''); }
    if (which !== 'labor') { setLaborDescription(''); setLaborHours(0); setLaborMinutes(30); setLaborPrice(''); setLaborPriceManual(false); }
    // Services are catalog data, not typed input — nothing to clear, but the
    // list is fetched lazily so it costs nothing until someone opens it.
    if (which === 'services' && services === null && !servicesBusy) loadServices();
    // Opening Labor/Misc means the tech isn't searching — clear stale results
    clearPartsSearch();
    setActivePanel(which);
  };

  const handlePartsFocus = () => { if (activePanel) closeActivePanel(); };

  // ── Handlers ───────────────────────────────────────────────────────────
  async function tryCodeLookup(code) {
    const r = await fetch(`${readBase}/item-lookup?code=${encodeURIComponent(code)}`, { credentials:'include', headers: authHeaders() });
    const data = await r.json();
    return data.found ? data.item : null;
  }

  async function handlePartsKeyDown(e) {
    if (e.key !== 'Enter') return;
    const code = partsQuery.trim();
    if (!code) return;
    // With catalogs on, start the slow catalog search (3-7 s) at the same
    // time as the quick inventory lookup instead of after it: a part the shop
    // does not stock then shows a second or so sooner (22 Oct). If the
    // lookup finds a stocked item, the catalog answer is simply dropped.
    const catalogPromise = includeCatalog && code.length >= 2 ? runCatalogSearch(code) : null;
    const item = await tryCodeLookup(code).catch(() => null);
    if (item) {
      await addResolvedPart(item, 1, true);
      setPartsQuery('');
      setPartsResults([]);
      dropCatalog();
    } else if (catalogPromise) {
      // Not a code the shop stocks: the vendor catalog results are on their way.
    } else {
      setPartsToastBad(true);
      setPartsToast(`No item matches ${code}`);
      setTimeout(() => setPartsToast(''), 4000);
    }
  }

  // Parts search surfaces both inventory and non_inventory items — same as
  // native Lightspeed. Which WRITE PATH each one takes is decided by the
  // server from its tax class and arrives as `isLabor`; item type does not
  // decide it. A non_inventory item with a goods tax class is a part.
  async function addResolvedPart(item, qty, isAutoScan = false) {
    setPartsBusy(true);
    try {
      let res;
      // The SERVER decides which endpoint an item belongs on, from its tax
      // class. Do not re-derive it here from itemType: that is what sent the
      // sealant (item 3224) to add-labor, where Lightspeed answered 422
      // "Non-labor Item cannot be added as a WorkorderLine" and the catch
      // below threw it away.
      if (item.isLabor) {
        res = await fetch(`${writeBase}/add-labor`, {
          method:'POST', credentials:'include',
          headers: authHeaders(true),
          body: JSON.stringify({ itemID: item.itemID, description: item.description, hours: 0, minutes: 0, priceOverride: item.price }),
        });
      } else {
        res = await fetch(`${writeBase}/add-item`, {
          method:'POST', credentials:'include',
          headers: authHeaders(true),
          body: JSON.stringify({ itemID: item.itemID, quantity: qty }),
        });
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || body.error || `Add failed (${res.status})`);
      }
      appendLine((await res.json().catch(() => ({}))).line);
      // Clear the search as soon as the write succeeds rather than waiting on
      // the reload. The POST is the part that matters — the reload is a
      // read-back, and GET /:id fans out across many Lightspeed calls that are
      // now serialized by the throttle, so awaiting it made every Add feel
      // slow. The tech gets their input back immediately; the line list
      // updates a moment later.
      setPartsQuery('');
      setPartsResults([]);
      setPartsBusy(false);
      if (isAutoScan) {
        setPartsToastBad(false);
        setPartsToast('Added — scan matched');
        setTimeout(() => setPartsToast(''), 2500);
      }
      reconcileAfterAdd();
      return;
    } catch (err) {
      console.error('Add part failed:', err);
      setPartsToastBad(true);
      setPartsToast(`Could not add ${item.description || 'that item'}. ${err.message || 'Try again.'}`);
      setTimeout(() => setPartsToast(''), 6000);
    }
    setPartsBusy(false);
  }

  // Labor button = freeform creation only. Catalog labor items are found via
  // the top Parts search (which returns both inventory and non_inventory items,
  // matching native Lightspeed). No second search here — it was redundant.
  async function loadServices() {
    setServicesBusy(true);
    setServicesError(false);
    try {
      const r = await fetch(`${API}/api/labor-items`, { credentials:'include', headers: authHeaders() });
      if (!r.ok) throw new Error('load failed');
      const data = await r.json();
      setServices(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Load services failed:', err);
      setServicesError(true);
      setServices([]);
    }
    setServicesBusy(false);
  }

  // Adds the catalog item itself, so the backend resolves the item's public note
  // and the line carries the shop's real service text — the same path the parts
  // search uses, not a freeform line that happens to share a name.
  //
  // Collapses on success and says what it added. This is the whole reason the
  // panel exists: Lightspeed leaves its picker open, acknowledges nothing, and
  // puts the close control far from where you just clicked. A tech adds one
  // service most of the time, so one tap should finish the job and tell them so.
  async function addService(item) {
    setAddingServiceId(item.id);
    try {
      const r = await fetch(`${writeBase}/add-labor`, {
        method:'POST', credentials:'include',
        headers: authHeaders(true),
        body: JSON.stringify({
          itemID: item.id,
          description: item.name,
          hours: 0,
          minutes: 0,
          priceOverride: item.price,
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || 'Add failed');
      appendLine(body.line);
      reconcileAfterAdd();
      // Collapse the item list back to the category chips, not the whole
      // panel — the tech stays oriented and can pick another category.
      setServiceCategory(null);
      setPartsToastBad(false);
      setPartsToast(`Added ${item.name} — $${Number(item.price || 0).toFixed(2)}`);
      setTimeout(() => setPartsToast(''), 3000);
    } catch (err) {
      console.error('Add service failed:', err);
      // Deliberately stays open on failure — collapsing would hide both the
      // error and the item they were trying to add.
      setPartsToastBad(true);
      setPartsToast(`Could not add ${item.name}. ${err.message || 'Try again.'}`);
      setTimeout(() => setPartsToast(''), 5000);
    }
    setAddingServiceId(null);
  }

  async function submitLabor() {
    if (!laborDescription.trim() || !laborPrice) return;
    setLaborBusy(true);
    try {
      const r = await fetch(`${writeBase}/add-labor`, {
        method:'POST', credentials:'include',
        headers: authHeaders(true),
        body: JSON.stringify({
          itemID: null,
          description: laborDescription.trim(),
          hours: parseFloat(laborHours) || 0,
          minutes: parseFloat(laborMinutes) || 0,
          priceOverride: parseFloat(laborPrice),
          discountID: laborDiscountID || 0,
          warranty: laborWarranty,
          tax: laborTax,
        }),
      });
      if (!r.ok) throw new Error('Add failed');
      appendLine((await r.json().catch(() => ({}))).line);
      reconcileAfterAdd();
      setLaborDescription(''); setLaborHours(0); setLaborMinutes(30); setLaborPrice(''); setLaborPriceManual(false);
      setLaborDiscountID(''); setLaborWarranty(false); setLaborTax(true);
      setActivePanel(null);
    } catch (err) { console.error('Add labor failed:', err); }
    setLaborBusy(false);
  }

  async function submitMisc() {
    if (!miscDescription.trim() || !miscPrice) return;
    setMiscBusy(true);
    try {
      // Backend now sends taxClassID + real qty/unitPrice to LS. No client-side
      // multiplication — LS remembers the breakdown, matching how it does for Parts.
      const r = await fetch(`${writeBase}/add-misc`, {
        method:'POST', credentials:'include',
        headers: authHeaders(true),
        body: JSON.stringify({ description: miscDescription.trim(), qty: parseInt(miscQty, 10) || 1, unitPrice: parseFloat(miscPrice),
          discountID: miscDiscountID || 0, warranty: miscWarranty, tax: miscTax }),
      });
      if (!r.ok) throw new Error('Add failed');
      appendLine((await r.json().catch(() => ({}))).line);
      reconcileAfterAdd();
      setMiscDescription(''); setMiscQty(1); setMiscPrice('');
      setMiscDiscountID(''); setMiscWarranty(false); setMiscTax(true);
      setActivePanel(null);
    } catch (err) { console.error('Add misc failed:', err); }
    setMiscBusy(false);
  }

  // Stock for the parts already on this ticket. Fetched separately from the
  // ticket detail so a slow or failed stock read never delays the ticket.
  const [lineStock, setLineStock] = useState({});
  const [employees, setEmployees] = useState([]);
  const [discounts, setDiscounts] = useState([]);

  useEffect(() => {
    const ids = [...new Set((detail?.lineItems || [])
      .filter(l => l.isInventory && l.itemID && String(l.itemID) !== '0')
      .map(l => String(l.itemID)))];
    if (!ids.length) { setLineStock({}); return; }
    fetch(`${readBase}/stock?itemIDs=${ids.join(',')}`, { credentials:'include', headers: authHeaders() })
      .then(r => r.json())
      .then(d => setLineStock(d.stock || {}))
      .catch(() => setLineStock({}));
  }, [detail, ticketId]);

  useEffect(() => {
    fetch(`${API}/api/tickets/employees/list`, { credentials:'include', headers: authHeaders() })
      .then(r => r.json())
      .then(data => setEmployees(Array.isArray(data) ? data : []))
      .catch(() => {});
    fetch(`${API}/api/tickets/discounts/list`, { credentials:'include', headers: authHeaders() })
      .then(r => r.json())
      .then(data => setDiscounts(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  async function updateItemQuantity(item, newQty) {
    try {
      await fetch(`${writeBase}/item-quantity/${item.id}`, {
        method:'PUT', credentials:'include',
        headers: authHeaders(true),
        body: JSON.stringify({ quantity: newQty }),
      });
      await loadTicketDetail();
    } catch (err) { console.error('Quantity update failed:', err); }
  }

  async function deleteLineItem(item) {
    if (!window.confirm(`Remove "${item.description}" from this ${noun}?`)) return;
    try {
      const type = item.isInventory ? 'item' : 'line';
      await fetch(`${writeBase}/line-item/${type}/${item.id}`, {
        method:'DELETE', credentials:'include',
        headers: authHeaders(),
      });
      await loadTicketDetail();
    } catch (err) { console.error('Delete line item failed:', err); }
  }

  function openLineEditor(item) {
    setEditingLine(item);
    setEditPriceManual(false);
    setEditForm({
      // On a catalog-backed line, `description` is the item NAME and `note`
      // holds the real line text — the checklist that prints on the customer's
      // receipt. Editing `description` and saving overwrites that note with
      // the item name. Freeform lines have no itemID; there description IS
      // the note.
      // On a part the description is the catalog name and is not ours to edit —
      // the note is the only free text. Same shape as a catalog-backed labor
      // line, which is why this reads the note there too.
      description: (item.lineType === 'parts' || (item.itemID && item.itemID !== '0') ? item.note : item.description) || '',
      qty: item.quantity || 1,
      unitPrice: item.unitPrice || '',
      hours: item.hours || 0,
      minutes: item.minutes || 0,
      employeeID: item.employeeID || '',
      discountID: item.discountID || '',
      warranty: !!item.warranty,
      tax: item.tax !== false,
    });
  }

  // Hours/minutes drive price at the shop labor rate, same as Add Labor.
  // Deliberately computed in the change handlers rather than an effect:
  // an effect would fire on open and silently rewrite the price of a line
  // someone had set by hand. Returns null to mean "leave the price alone."
  const recalcEditPrice = (hours, minutes) => {
    if (editPriceManual || !laborRate) return null;
    const totalHours = (parseFloat(hours) || 0) + (parseFloat(minutes) || 0) / 60;
    return totalHours > 0 ? (totalHours * laborRate).toFixed(2) : '';
  };

  // A labor line with time but no price -- typically one a tech logged in
  // Lightspeed -- gets the shop-rate price OFFERED, never applied on open
  // (22 Oct). Opening a line to look at it must not change what the customer
  // pays, and some $0 lines are deliberate (warranty, comped work). Null when
  // there is nothing to suggest.
  // Shown under a labor price box when the shop has no hourly rate, so typing
  // hours and seeing no price is explained rather than looking broken. No
  // default rate on purpose: a wrong rate is worse than none.
  const noRateHint = laborRateLoaded && !laborRate && (
    <div style={{ fontSize:11, color:T.textSub, marginTop:-2, marginBottom:10, lineHeight:1.45 }}>
      No hourly labor rate is set, so prices are not calculated from time. An Administrator can set one in Settings, Shop.
    </div>
  );

  const editSuggestion = editingLine
    ? laborPriceSuggestion({ ...editForm, lineType: editingLine.lineType, isTimerLine: editingLine.isTimerLine }, laborRate)
    : null;

  async function submitLineEdit() {
    if (!editingLine) return;
    const kind = editingLine.lineType || 'labor';
    setEditBusy(true);
    try {
      // Parts are WorkorderItems, not WorkorderLines — a different Lightspeed
      // object and a different route. They accept discount, warranty, tax and
      // note under identical names, confirmed against a live ticket rather than
      // assumed, but have no hours, no price override and no per-line employee.
      // The payload is deliberately narrower rather than the labor one carrying
      // fields the API would reject.
      if (kind === 'parts') {
        const r = await fetch(`${writeBase}/item-quantity/${editingLine.id}`, {
          method:'PUT', credentials:'include',
          headers: authHeaders(true),
          body: JSON.stringify({
            quantity: parseInt(editForm.qty, 10) || 1,
            discountID: editForm.discountID,
            warranty: editForm.warranty,
            tax: editForm.tax,
            note: editForm.description,
          }),
        });
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Save failed');
        await loadTicketDetail();
        setEditingLine(null);
        setEditBusy(false);
        return;
      }

      const payload = {
        lineType: kind,
        description: editForm.description.trim(),
        unitPrice: parseFloat(editForm.unitPrice) || 0,
        employeeID: editForm.employeeID,
        discountID: editForm.discountID,
        warranty: editForm.warranty,
        tax: editForm.tax,
      };
      if (kind === 'misc') payload.quantity = parseInt(editForm.qty, 10) || 1;
      if (kind === 'labor') { payload.hours = parseInt(editForm.hours, 10) || 0; payload.minutes = parseInt(editForm.minutes, 10) || 0; }
      await fetch(`${writeBase}/line/${editingLine.id}`, {
        method:'PUT', credentials:'include',
        headers:{'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('sb_token')}`},
        body: JSON.stringify(payload),
      });
      await loadTicketDetail();
      setEditingLine(null);
    } catch (err) { console.error('Edit line failed:', err); }
    setEditBusy(false);
  }

  const panelStyle = {
    background: isDark ? '#141d30' : '#fbfbf7',
    border:`1px solid ${isDark ? '#243050' : '#e0e0d8'}`,
    borderRadius:10, padding:'16px 20px',
  };

  // ── Add Item panel ─────────────────────────────────────────────────────
  // Rendered inside TicketCard via addItemSlot so it lands above the line items.
  const addItemPanel = !checkedOut && (
    <div style={{ ...panelStyle, marginBottom: 14 }}>
      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
        <div style={{ position:'relative', flex:1 }}>
          <input
            value={partsQuery}
            onChange={e => setPartsQuery(e.target.value)}
            onKeyDown={handlePartsKeyDown}
            onFocus={handlePartsFocus}
            placeholder="Add Items — scan a barcode/System ID, or type a part name…"
            style={{ width:'100%', boxSizing:'border-box', background: isDark ? '#0f1724' : '#fff',
              border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`, borderRadius:6, padding:'8px 32px 8px 10px',
              color:T.text, fontSize:12, fontFamily:'Arial, sans-serif', outline:'none' }}
          />
          {partsQuery && (
            <button
              onClick={clearPartsSearch}
              title="Clear search"
              style={{ position:'absolute', right:6, top:'50%', transform:'translateY(-50%)',
                background:'transparent', border:'none', cursor:'pointer',
                color: isDark ? '#5a6a8a' : '#aaa', fontSize:16, lineHeight:1,
                padding:'2px 6px', borderRadius:4 }}>
              ✕
            </button>
          )}
        </div>
        <button onClick={() => toggleActivePanel('services')}
          title="Pick a service from the shop's catalog"
          style={{ height:34, padding:'0 16px', fontSize:12, fontWeight:600, borderRadius:6, cursor:'pointer', whiteSpace:'nowrap',
            background: activePanel === 'services' ? '#3498db' : 'transparent',
            color: activePanel === 'services' ? '#fff' : T.textSub,
            border:`1px solid ${activePanel === 'services' ? '#3498db' : (isDark ? '#2a3550' : '#d8d8d0')}`,
            fontFamily:'Arial, sans-serif' }}>
          + Services
        </button>
        <button onClick={() => toggleActivePanel('labor')}
          title="Create a custom labor line"
          style={{ height:34, padding:'0 16px', fontSize:12, fontWeight:600, borderRadius:6, cursor:'pointer', whiteSpace:'nowrap',
            background: activePanel === 'labor' ? '#3498db' : 'transparent',
            color: activePanel === 'labor' ? '#fff' : T.textSub,
            border:`1px solid ${activePanel === 'labor' ? '#3498db' : (isDark ? '#2a3550' : '#d8d8d0')}`,
            fontFamily:'Arial, sans-serif' }}>
          + Labor
        </button>
        {handParts && (
          <button onClick={() => toggleActivePanel('hand')}
            title="A part you have never carried and no vendor catalog lists. It is marked to order."
            style={{ height:34, padding:'0 16px', fontSize:12, fontWeight:600, borderRadius:6, cursor:'pointer', whiteSpace:'nowrap',
              background: activePanel === 'hand' ? '#3498db' : 'transparent',
              color: activePanel === 'hand' ? '#fff' : T.textSub,
              border:`1px solid ${activePanel === 'hand' ? '#3498db' : (isDark ? '#2a3550' : '#d8d8d0')}`,
              fontFamily:'Arial, sans-serif' }}>
            + Order part
          </button>
        )}
        <button onClick={() => toggleActivePanel('misc')}
          title="Add a one-off item not in the catalog"
          style={{ height:34, padding:'0 16px', fontSize:12, fontWeight:600, borderRadius:6, cursor:'pointer', whiteSpace:'nowrap',
            background: activePanel === 'misc' ? '#3498db' : 'transparent',
            color: activePanel === 'misc' ? '#fff' : T.textSub,
            border:`1px solid ${activePanel === 'misc' ? '#3498db' : (isDark ? '#2a3550' : '#d8d8d0')}`,
            fontFamily:'Arial, sans-serif' }}>
          + Misc
        </button>
      </div>

      <label style={{ display:'inline-flex', alignItems:'center', gap:6, marginTop:8, fontSize:11, color:T.textSub, cursor:'pointer' }}
        title="Also search the vendor catalogs in Lightspeed (QBP and others). Each search takes a few seconds, so it runs when you press Enter.">
        <input type="checkbox" checked={includeCatalog} onChange={e => toggleCatalog(e.target.checked)}
          style={{ accentColor:'#3498db', width:13, height:13, margin:0 }} />
        Include vendor catalogs
      </label>
      {includeCatalog && partsQuery.trim().length >= 2 && !catalogBusy && (
        <button type="button" onClick={() => handlePartsKeyDown({ key: 'Enter' })}
          style={{ marginLeft:10, height:24, padding:'0 10px', fontSize:11, fontWeight:600, borderRadius:5, cursor:'pointer',
            background:'transparent', color:'#3498db', border:'1px solid #3498db', fontFamily:'Arial, sans-serif' }}>
          Search vendor catalogs for “{partsQuery.trim().slice(0, 30)}” ↵
        </button>
      )}

      {partsToast && <div style={{ fontSize:11, color: partsToastBad ? '#e74c3c' : '#2ecc71', marginTop:8, fontWeight:600 }}>{partsToast}</div>}
      {partsSearching && <div style={{ fontSize:12, color:T.textSub, fontStyle:'italic', marginTop:8 }}>Searching…</div>}
      {/* Typing only searches item names. A part number or UPC needs Enter,
          which was not obvious from the screen (Manny, 22 Oct): say so the
          moment the name search comes back empty. */}
      {!partsSearching && !catalogBusy && !catalog && partsQuery.trim().length >= 2 && partsResults.length === 0 && (
        <div style={{ fontSize:12, color:T.textSub, marginTop:8 }}>
          No item names match. <b style={{ color:T.text }}>Press Enter</b> to look “{partsQuery.trim().slice(0, 40)}” up as a part number, UPC or SKU{includeCatalog ? ', in your inventory and the vendor catalogs' : ''}.
          {!includeCatalog && <> Not stocked? Tick <b style={{ color:T.text }}>Include vendor catalogs</b> first.</>}
        </div>
      )}
      {partsResults.length > 0 && (
        <div style={{ marginTop:8 }}>
          {partsResults.map(r => (
            <div key={r.itemID} style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
              padding:'8px 0', borderBottom:`1px solid ${isDark ? '#243050' : '#e0e0d8'}`, gap:8 }}>
              <div style={{ minWidth:0, flex:1 }}>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:T.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.description}</div>
                  {r.isLabor && (
                    <span style={{ fontSize:9, fontWeight:700, padding:'2px 6px', borderRadius:4, whiteSpace:'nowrap',
                      background: isDark ? '#0f1e30' : '#e8f0f8', color:'#3498db' }}>LABOR</span>
                  )}
                </div>
                <div style={{ fontSize:11, color:T.textSub, display:'flex', alignItems:'center', gap:8 }}>
        <span>${r.price.toFixed(2)}</span>
        {r.stock && (() => {
          const want = partsQty[r.itemID] || 1;
          const short = r.stock.available < want;
          return (
            <span style={{ color: short ? '#e74c3c' : (r.stock.available > 0 ? '#2ecc71' : T.textSub), fontWeight: short ? 700 : 500 }}>
              {short && '⚠ '}
              {r.stock.available} available
              {r.stock.reserved > 0 && (
                <span style={{ color:T.textSub, fontWeight:400 }}>
                  {' '}({r.stock.qoh} in stock, {r.stock.reserved} reserved)
                </span>
              )}
            </span>
          );
        })()}
      </div>
              </div>
              {!r.isLabor && (
                <input type="number" min="1" value={partsQty[r.itemID] || 1}
                  onChange={e => setPartsQty(q => ({ ...q, [r.itemID]: parseInt(e.target.value,10) || 1 }))}
                  style={{ width:44, height:30, fontSize:12, textAlign:'center',
                    background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`, borderRadius:5, color:T.text }} />
              )}
              <button disabled={partsBusy} onClick={() => {
        const want = partsQty[r.itemID] || 1;
        if (r.stock && r.stock.available < want) {
          const detail = r.stock.reserved > 0
            ? `${r.stock.qoh} in stock but ${r.stock.reserved} already reserved for other tickets, so ${r.stock.available} ${r.stock.available === 1 ? 'is' : 'are'} actually available.`
            : `Only ${r.stock.available} available.`;
          if (!window.confirm(`${r.description}\n\n${detail}\n\nAdd ${want} anyway?`)) return;
        }
        addResolvedPart(r, want);
      }}
                style={{ height:30, padding:'0 12px', fontSize:12, borderRadius:5, cursor:'pointer',
                  background:'#3498db', color:'#fff', border:'none', fontFamily:'Arial, sans-serif' }}>
                Add
              </button>
            </div>
          ))}
        </div>
      )}

      {includeCatalog && (catalogBusy || catalogError || catalog) && (() => {
        const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('en-US', { month:'short', year:'numeric' }); } catch (_) { return ''; } };
        const stale = (iso) => { const t = Date.parse(iso); return Number.isFinite(t) && Date.now() - t > 180 * 864e5; };
        const rows = catalog ? (showOtherVendors ? [...catalog.results, ...catalog.other] : catalog.results) : [];
        return (
          <div style={{ marginTop:10 }}>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:0.6, textTransform:'uppercase', color:T.textSub, marginBottom:4 }}>From vendor catalogs</div>
            {catalogBusy && (
              <div>
                <style>{'@keyframes sbCatalogSlide{0%{left:-35%}100%{left:100%}}@keyframes sbCatalogSpin{to{transform:rotate(360deg)}}'}</style>
                <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:T.text }}>
                  <span aria-hidden="true" style={{ width:12, height:12, borderRadius:'50%', border:'2px solid #3498db', borderTopColor:'transparent', display:'inline-block', animation:'sbCatalogSpin 0.8s linear infinite' }} />
                  Searching the vendor catalogs in Lightspeed… {catalogElapsed > 0 ? `${catalogElapsed}s` : ''}
                  <span style={{ color:T.textSub }}>{catalogElapsed >= 4 ? 'usually 3 to 7 seconds' : ''}</span>
                </div>
                <div style={{ position:'relative', height:3, borderRadius:2, overflow:'hidden', marginTop:6, background: isDark ? '#243050' : '#e0e0d8' }}>
                  <div style={{ position:'absolute', top:0, bottom:0, width:'35%', borderRadius:2, background:'#3498db', animation:'sbCatalogSlide 1.2s ease-in-out infinite' }} />
                </div>
              </div>
            )}
            {catalogError && <div style={{ fontSize:12, color:'#e74c3c' }}>{catalogError}</div>}
            {catalog && !rows.length && <div style={{ fontSize:12, color:T.textSub }}>Nothing in your vendors' catalogs matches.</div>}
            {rows.slice(0, 40).map(r => (
              <div key={r.catalogItemID} style={{ padding:'7px 0', borderBottom:`1px solid ${isDark ? '#243050' : '#e0e0d8'}` }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:T.text, minWidth:0, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.description}</div>
                  <div style={{ fontSize:13, fontWeight:600, color:T.text, whiteSpace:'nowrap' }}>{r.msrp > 0 ? `$${r.msrp.toFixed(2)}` : 'no price'}</div>
                  {(
                    <>
                      <input type="number" min="1" value={catalogQty[r.catalogItemID] || 1}
                        onChange={e => setCatalogQty(qv => ({ ...qv, [r.catalogItemID]: parseInt(e.target.value, 10) || 1 }))}
                        style={{ width:44, height:28, fontSize:12, textAlign:'center', background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`, borderRadius:5, color:T.text }} />
                      <button disabled={partsBusy} onClick={() => addCatalogPart(r)}
                        title={noun === 'quote' ? 'Adds it to this quote, marked to order. Nothing is created in Lightspeed unless the quote is approved.' : 'Adds it to this ticket. If it is not already an item in Lightspeed, Spinboard creates the item from the catalog first.'}
                        style={{ height:28, padding:'0 12px', fontSize:12, borderRadius:5, cursor:'pointer', background:'#3498db', color:'#fff', border:'none', fontFamily:'Arial, sans-serif' }}>
                        {catalogNeedsPricing(r) ? 'Price & add' : 'Add'}
                      </button>
                    </>
                  )}
                </div>
                <div style={{ fontSize:11, color:T.textSub, display:'flex', flexWrap:'wrap', gap:'2px 10px', marginTop:2 }}>
                  <span>{r.vendor}</span>
                  {r.vendorNumber && <span>part {r.vendorNumber}</span>}
                  {r.cost > 0 && <span>cost ${r.cost.toFixed(2)}</span>}
                  <span style={{ color: r.stock > 0 ? '#2ecc71' : T.textSub }}>{r.stock > 0 ? `${r.stock} at vendor` : 'stock unknown'}</span>
                  {r.catalogUpdated && <span style={{ color: stale(r.catalogUpdated) ? '#e67e22' : T.textSub }}>catalog {fmtDate(r.catalogUpdated)}</span>}
                </div>
                {pricing && pricing.id === r.catalogItemID && (() => {
                  const p = pricing;
                  const at = pricedAt(p);
                  const cost = Number(p.cost) || 0;
                  const set = (patch) => setPricing(x => ({ ...x, ...patch }));
                  const opt = (key, label) => (
                    <label key={key} style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:T.text, padding:'3px 0', cursor:'pointer' }}>
                      <input type="radio" name={`price-${r.catalogItemID}`} checked={p.choice === key} onChange={() => set({ choice: key })} style={{ margin:0 }} />
                      {label}
                    </label>
                  );
                  const box = { height:26, fontSize:12, background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`, borderRadius:5, color:T.text, padding:'0 6px' };
                  return (
                    <div style={{ marginTop:8, padding:'10px 12px', borderRadius:8, border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`, background: isDark ? '#0f1724' : '#fff' }}>
                      <div style={{ fontSize:12, fontWeight:600, color:T.text, marginBottom:6 }}>
                        {!(r.msrp > 0) ? 'This catalog has no MSRP for this part. Choose a price.' : `This catalog was last updated ${fmtDate(r.catalogUpdated)}. Check the price.`}
                      </div>
                      {p.loading && <div style={{ fontSize:12, color:T.textSub, fontStyle:'italic' }}>Looking for this part in your other vendors' catalogs…</div>}
                      {p.error && <div style={{ fontSize:12, color:'#e74c3c' }}>{p.error}</div>}
                      {p.opts && (
                        <>
                          {p.opts.elsewhere.map((e, i) => opt(`elsewhere-${i}`, <>MSRP from {e.vendor}: <b>${e.msrp.toFixed(2)}</b></>))}
                          {p.opts.msrp > 0 && opt('catalog', <>This catalog's MSRP: <b>${p.opts.msrp.toFixed(2)}</b> <span style={{ color:'#e67e22' }}>(catalog {fmtDate(p.opts.catalogUpdated)})</span></>)}
                          {opt('keystone', <>Keystone, cost × 2{cost > 0 ? <>: <b>${(Math.round(cost * 200) / 100).toFixed(2)}</b></> : <span style={{ color:T.textSub }}> (needs a cost)</span>}</>)}
                          <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:T.text, padding:'3px 0', cursor:'pointer' }}>
                            <input type="radio" name={`price-${r.catalogItemID}`} checked={p.choice === 'margin'} onChange={() => set({ choice: 'margin' })} style={{ margin:0 }} />
                            Margin
                            <input type="number" min="1" max="99" value={p.margin} placeholder="40"
                              onChange={e => { const v = e.target.value; set({ margin: v, choice: 'margin' }); try { localStorage.setItem('sb-catalog-margin', v); } catch (_) { /* per device */ } }}
                              style={{ ...box, width:52 }} />%
                            {p.choice === 'margin' && at != null && <b>${at.toFixed(2)}</b>}
                            {cost <= 0 && <span style={{ color:T.textSub }}>(needs a cost)</span>}
                          </label>
                          <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:T.text, padding:'3px 0', cursor:'pointer' }}>
                            <input type="radio" name={`price-${r.catalogItemID}`} checked={p.choice === 'manual'} onChange={() => set({ choice: 'manual' })} style={{ margin:0 }} />
                            Enter a price $
                            <input type="number" min="0" step="0.01" value={p.manual}
                              onChange={e => set({ manual: e.target.value, choice: 'manual' })}
                              style={{ ...box, width:80 }} />
                          </label>
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, fontSize:12, color:T.textSub }}>
                            Cost $
                            <input type="number" min="0" step="0.01" value={p.cost} onChange={e => set({ cost: e.target.value })} style={{ ...box, width:80 }} />
                            <span>saved on the new item</span>
                          </div>
                          <div style={{ display:'flex', gap:8, marginTop:10 }}>
                            <button disabled={partsBusy || at == null} onClick={() => addCatalogPart(r, { price: at, cost: Number(p.cost) || undefined })}
                              style={{ height:28, padding:'0 12px', fontSize:12, borderRadius:5, cursor: at == null ? 'default' : 'pointer', background: at == null ? (isDark ? '#243050' : '#d0d0c8') : '#3498db', color:'#fff', border:'none', fontFamily:'Arial, sans-serif' }}>
                              {at == null ? 'Choose a price' : `Create item at $${at.toFixed(2)} and add`}
                            </button>
                            <button onClick={() => setPricing(null)}
                              style={{ height:28, padding:'0 12px', fontSize:12, borderRadius:5, cursor:'pointer', background:'transparent', color:T.textSub, border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`, fontFamily:'Arial, sans-serif' }}>
                              Cancel
                            </button>
                          </div>
                          <div style={{ fontSize:11, color:T.textSub, marginTop:6 }}>The price becomes the item's price in Lightspeed. If you already have this part as an item, that item and its price are used instead.</div>
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>
            ))}
            {catalog && catalog.other.length > 0 && !showOtherVendors && (
              <button onClick={() => setShowOtherVendors(true)}
                style={{ marginTop:6, background:'none', border:'none', padding:0, cursor:'pointer', fontSize:11, color:'#3498db', textDecoration:'underline' }}>
                Show {catalog.other.length} from vendors you don't use
              </button>
            )}
            {catalog && rows.length > 0 && (
              <div style={{ fontSize:11, color:T.textSub, marginTop:6 }}>
                Adding a catalog part creates it as an item in Lightspeed (price, cost, UPC, vendor and part number from the catalog), unless you already have it. With no MSRP or an old catalog, you choose the price first.
              </div>
            )}
          </div>
        );
      })()}

      {activePanel === 'services' && (
        <div style={{ marginTop:10, paddingTop:10, borderTop:`1px solid ${isDark ? '#243050' : '#e0e0d8'}` }}>
          {servicesBusy ? (
            <div style={{ fontSize:12, color:T.textSub, fontStyle:'italic' }}>Loading services…</div>
          ) : servicesError ? (
            <div style={{ fontSize:12, color:'#e74c3c' }}>
              Could not load services.{' '}
              <button onClick={loadServices}
                style={{ background:'transparent', border:'none', color:'#3498db', cursor:'pointer', fontSize:12, padding:0, textDecoration:'underline' }}>
                Retry
              </button>
            </div>
          ) : !services || services.length === 0 ? (
            <div style={{ fontSize:12, color:T.textSub }}>
              No service items found in the Lightspeed catalog.
            </div>
          ) : (() => {
            // Group by the shop's own Lightspeed categories. Uncategorised items
            // are surfaced rather than dropped — an invisible service is worse
            // than an untidy chip.
            const groups = {};
            for (const it of services) {
              const key = it.category || 'Uncategorised';
              (groups[key] = groups[key] || []).push(it);
            }
            const names = Object.keys(groups).sort((a, b) =>
              a === 'Uncategorised' ? 1 : b === 'Uncategorised' ? -1 : a.localeCompare(b));
            // No category auto-opens. The resting state is chips only — that is
            // also where an add returns you to, so the panel never sits open
            // showing a list the tech is done with.
            const active = names.includes(serviceCategory) ? serviceCategory : null;
            const shown = active ? (groups[active] || []).slice().sort((a, b) => a.name.localeCompare(b.name)) : [];
            const chipIdle = isDark ? '#2a3550' : '#d8d8d0';
            return (
              <>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom: active ? 10 : 0 }}>
                  {names.map(n => (
                    <button key={n}
                      // Clicking the open category closes it, same as the
                      // + Services toggle itself.
                      onClick={() => setServiceCategory(n === active ? null : n)}
                      onMouseEnter={e => {
                        if (n === active) return;
                        e.currentTarget.style.background = isDark ? '#1c2740' : '#eef4fa';
                        e.currentTarget.style.borderColor = '#3498db';
                        e.currentTarget.style.color = '#3498db';
                      }}
                      onMouseLeave={e => {
                        if (n === active) return;
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.borderColor = chipIdle;
                        e.currentTarget.style.color = T.textSub;
                      }}
                      style={{ height:28, padding:'0 12px', fontSize:12, borderRadius:14, cursor:'pointer',
                        whiteSpace:'nowrap', fontFamily:'Arial, sans-serif', transition:'background 0.12s, border-color 0.12s, color 0.12s',
                        fontWeight: n === active ? 700 : 500,
                        background: n === active ? '#3498db' : 'transparent',
                        color: n === active ? '#fff' : T.textSub,
                        border:`1px solid ${n === active ? '#3498db' : chipIdle}` }}>
                      {n}
                      <span style={{ marginLeft:6, opacity:0.7, fontWeight:500 }}>{groups[n].length}</span>
                    </button>
                  ))}
                </div>
                {active && (
                <div style={{ maxHeight:260, overflowY:'auto' }}>
                  {shown.map(it => (
                    <div key={it.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8,
                      padding:'7px 0', borderBottom:`1px solid ${isDark ? '#243050' : '#e0e0d8'}` }}>
                      <div style={{ minWidth:0, flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:600, color:T.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{it.name}</div>
                        <div style={{ fontSize:11, color:T.textSub }}>${Number(it.price || 0).toFixed(2)}</div>
                      </div>
                      <button disabled={addingServiceId === it.id} onClick={() => addService(it)}
                        style={{ height:30, padding:'0 12px', fontSize:12, borderRadius:5, cursor: addingServiceId === it.id ? 'wait' : 'pointer',
                          background:'#3498db', color:'#fff', border:'none', fontFamily:'Arial, sans-serif',
                          opacity: addingServiceId === it.id ? 0.7 : 1 }}>
                        {addingServiceId === it.id ? 'Adding…' : 'Add'}
                      </button>
                    </div>
                  ))}
                </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {activePanel === 'labor' && (
        <div style={{ marginTop:12, padding:'12px 14px', borderRadius:8,
          background: isDark ? '#0f2740' : '#eaf4fc',
          borderLeft: `3px solid #3498db` }}>
          <div style={{ fontSize:10, fontWeight:700, color: isDark ? '#7ab8e8' : '#1f6ba8',
            textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>
            New labor line
          </div>
          <textarea
            autoFocus
            value={laborDescription}
            onChange={e => setLaborDescription(e.target.value)}
            placeholder="Describe the labor performed…"
            rows={3}
            style={{ width:'100%', boxSizing:'border-box', background: isDark ? '#0f1724' : '#fff',
              border:`1px solid ${isDark ? '#2a3550' : '#c8dcf0'}`, borderRadius:6, padding:'8px 10px',
              color:T.text, fontSize:12, fontFamily:'Arial, sans-serif', marginBottom:8, outline:'none', resize:'vertical' }}
          />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:8 }}>
            <div>
              <div style={{ fontSize:10, color:T.textSub, marginBottom:3 }}>Hours</div>
              <input type="number" min="0" value={laborHours}
                onChange={e => setLaborHours(e.target.value)}
                style={{ width:'100%', boxSizing:'border-box', height:30, fontSize:12, textAlign:'center',
                  background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#c8dcf0'}`, borderRadius:5, color:T.text }} />
            </div>
            <div>
              <div style={{ fontSize:10, color:T.textSub, marginBottom:3 }}>Minutes</div>
              <input type="number" min="0" value={laborMinutes}
                onChange={e => setLaborMinutes(e.target.value)}
                style={{ width:'100%', boxSizing:'border-box', height:30, fontSize:12, textAlign:'center',
                  background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#c8dcf0'}`, borderRadius:5, color:T.text }} />
            </div>
            <div>
              <div style={{ fontSize:10, color:T.textSub, marginBottom:3 }}>Price</div>
              <input type="number" min="0" value={laborPrice}
                onChange={e => { setLaborPriceManual(true); setLaborPrice(e.target.value); }}
                style={{ width:'100%', boxSizing:'border-box', height:30, fontSize:12, textAlign:'center',
                  background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#c8dcf0'}`, borderRadius:5, color:T.text }} />
            </div>
          </div>
          {noRateHint}
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:10, color:T.textSub, marginBottom:3 }}>Discount</div>
            <select value={laborDiscountID} onChange={e => setLaborDiscountID(e.target.value)}
              style={{ width:'100%', boxSizing:'border-box', height:30, fontSize:12,
                background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#c8dcf0'}`,
                borderRadius:5, color:T.text, cursor:'pointer', fontFamily:'inherit', outline:'none' }}>
              <option value="" style={{ background: isDark ? '#0f1724' : '#fff', color:T.text }}>No discount</option>
              {discounts.map(d => (
                <option key={d.id} value={d.id} style={{ background: isDark ? '#0f1724' : '#fff', color:T.text }}>
                  {d.name}{d.percent ? ` (${(d.percent * 100).toFixed(d.percent * 100 % 1 ? 1 : 0)}%)` : ''}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display:'flex', gap:18, marginBottom:10 }}>
            <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:T.text, cursor:'pointer' }}>
              <input type="checkbox" checked={laborWarranty}
                onChange={e => setLaborWarranty(e.target.checked)} style={{ cursor:'pointer' }} />
              Warranty
            </label>
            <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:T.text, cursor:'pointer' }}>
              <input type="checkbox" checked={laborTax}
                onChange={e => setLaborTax(e.target.checked)} style={{ cursor:'pointer' }} />
              Tax
            </label>
          </div>
          {laborWarranty && (
            <div style={{ fontSize:11, color:'#e67e22', marginBottom:10, lineHeight:1.5 }}>
              Warranty zeroes out the charge for this line.
            </div>
          )}
          <button disabled={laborBusy || !laborDescription.trim() || !laborPrice} onClick={submitLabor}
            style={{ width:'100%', height:34, fontSize:12, borderRadius:5, cursor:'pointer',
              background:'#3498db', color:'#fff', border:'none', fontFamily:'Arial, sans-serif' }}>
            {laborBusy ? 'Adding…' : 'Add labor line'}
          </button>
        </div>
      )}

      {activePanel === 'hand' && (() => {
        const box = { width:'100%', boxSizing:'border-box', height:30, fontSize:12, padding:'0 8px',
          background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#cfe0f0'}`, borderRadius:5, color:T.text };
        const field = (label, key, extra = {}) => (
          <div>
            <div style={{ fontSize:10, color:T.textSub, marginBottom:3 }}>{label}</div>
            <input value={hand[key]} onChange={e => setHand(h => ({ ...h, [key]: e.target.value }))} style={box} {...extra} />
          </div>
        );
        const ok = hand.description.trim() && parseFloat(hand.unitPrice) > 0;
        return (
          <div style={{ marginTop:12, padding:'12px 14px', borderRadius:8, background: isDark ? '#0f1e30' : '#eef5fc', borderLeft:'3px solid #3498db' }}>
            <div style={{ fontSize:10, fontWeight:700, color:'#3498db', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>
              Part to order
            </div>
            <div style={{ marginBottom:8 }}>{field('Description', 'description', { autoFocus: true, placeholder: 'e.g. SRAM AXS rear derailleur hanger, UDH' })}</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
              {field('Vendor', 'vendor', { placeholder: 'e.g. QBP' })}
              {field('Vendor part number', 'vendorPart')}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:8 }}>
              {field('Cost', 'unitCost', { type: 'number', min: '0', step: '0.01' })}
              {field('Price', 'unitPrice', { type: 'number', min: '0', step: '0.01' })}
              {field('Quantity', 'quantity', { type: 'number', min: '1' })}
            </div>
            <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:T.text, cursor:'pointer', marginBottom:10 }}>
              <input type="checkbox" checked={hand.tax} onChange={e => setHand(h => ({ ...h, tax: e.target.checked }))} /> Tax
            </label>
            {handError && <div style={{ fontSize:12, color:'#e74c3c', marginBottom:8 }}>{handError}</div>}
            <div style={{ display:'flex', gap:8 }}>
              <button disabled={!ok || handBusy} onClick={submitHand}
                style={{ height:30, padding:'0 14px', fontSize:12, fontWeight:600, borderRadius:5, border:'none', cursor: ok ? 'pointer' : 'default',
                  background: ok ? '#3498db' : (isDark ? '#243050' : '#d0d0c8'), color:'#fff', fontFamily:'Arial, sans-serif' }}>
                {handBusy ? 'Adding…' : 'Add part to order'}
              </button>
              <button onClick={closeActivePanel}
                style={{ height:30, padding:'0 14px', fontSize:12, borderRadius:5, cursor:'pointer', background:'transparent', color:T.textSub,
                  border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`, fontFamily:'Arial, sans-serif' }}>
                Cancel
              </button>
            </div>
            <div style={{ fontSize:11, color:T.textSub, marginTop:6 }}>Marked to order. It becomes an item in Lightspeed only if the quote is approved.</div>
          </div>
        );
      })()}

      {activePanel === 'misc' && (
        <div style={{ marginTop:12, padding:'12px 14px', borderRadius:8,
          background: isDark ? '#2a1f0a' : '#fdf3e2',
          borderLeft: `3px solid #e67e22` }}>
          <div style={{ fontSize:10, fontWeight:700, color: isDark ? '#f0b060' : '#a05a0a',
            textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>
            New misc line
          </div>
          <textarea
            autoFocus
            value={miscDescription}
            onChange={e => setMiscDescription(e.target.value)}
            placeholder="Describe the item (e.g. shop supply fee)"
            rows={2}
            style={{ width:'100%', boxSizing:'border-box', background: isDark ? '#0f1724' : '#fff',
              border:`1px solid ${isDark ? '#2a3550' : '#f0d9ae'}`, borderRadius:6, padding:'8px 10px',
              color:T.text, fontSize:12, fontFamily:'Arial, sans-serif', marginBottom:8, outline:'none', resize:'vertical' }}
          />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
            <div>
              <div style={{ fontSize:10, color:T.textSub, marginBottom:3 }}>Quantity</div>
              <input type="number" min="1" value={miscQty} onChange={e => setMiscQty(e.target.value)}
                style={{ width:'100%', boxSizing:'border-box', height:30, fontSize:12, textAlign:'center',
                  background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#f0d9ae'}`, borderRadius:5, color:T.text }} />
            </div>
            <div>
              <div style={{ fontSize:10, color:T.textSub, marginBottom:3 }}>Unit price</div>
              <input type="number" min="0" value={miscPrice} onChange={e => setMiscPrice(e.target.value)}
                style={{ width:'100%', boxSizing:'border-box', height:30, fontSize:12, textAlign:'center',
                  background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#f0d9ae'}`, borderRadius:5, color:T.text }} />
            </div>
          </div>
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:10, color:T.textSub, marginBottom:3 }}>Discount</div>
            <select value={miscDiscountID} onChange={e => setMiscDiscountID(e.target.value)}
              style={{ width:'100%', boxSizing:'border-box', height:30, fontSize:12,
                background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#f0d9ae'}`,
                borderRadius:5, color:T.text, cursor:'pointer', fontFamily:'inherit', outline:'none' }}>
              <option value="" style={{ background: isDark ? '#0f1724' : '#fff', color:T.text }}>No discount</option>
              {discounts.map(d => (
                <option key={d.id} value={d.id} style={{ background: isDark ? '#0f1724' : '#fff', color:T.text }}>
                  {d.name}{d.percent ? ` (${(d.percent * 100).toFixed(d.percent * 100 % 1 ? 1 : 0)}%)` : ''}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display:'flex', gap:18, marginBottom:10 }}>
            <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:T.text, cursor:'pointer' }}>
              <input type="checkbox" checked={miscWarranty}
                onChange={e => setMiscWarranty(e.target.checked)} style={{ cursor:'pointer' }} />
              Warranty
            </label>
            <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:T.text, cursor:'pointer' }}>
              <input type="checkbox" checked={miscTax}
                onChange={e => setMiscTax(e.target.checked)} style={{ cursor:'pointer' }} />
              Tax
            </label>
          </div>
          {miscWarranty && (
            <div style={{ fontSize:11, color:'#e67e22', marginBottom:10, lineHeight:1.5 }}>
              Warranty zeroes out the charge for this line.
            </div>
          )}
          <button disabled={miscBusy || !miscDescription.trim() || !miscPrice} onClick={submitMisc}
            style={{ width:'100%', height:34, fontSize:12, borderRadius:5, cursor:'pointer',
              background:'#e67e22', color:'#fff', border:'none', fontFamily:'Arial, sans-serif' }}>
            {miscBusy ? 'Adding…' : 'Add misc line'}
          </button>
        </div>
      )}
    </div>
  );

  // The line editor. Fixed-position, so it can be rendered anywhere.
  const lineEditorModal = editingLine && (
      <div onClick={() => setEditingLine(null)}
        style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', zIndex:1100,
          display:'flex', alignItems:'center', justifyContent:'center', padding:'24px 16px' }}>
        <div onClick={e => e.stopPropagation()}
          style={{ width:'100%', maxWidth:560, background: isDark ? '#141d30' : '#fff',
            border:`1px solid ${isDark ? '#243050' : '#e0e0d8'}`, borderRadius:12, padding:'18px 20px',
            boxShadow:'0 12px 32px rgba(0,0,0,0.25)' }}>
          <div style={{ fontSize:11, color: isDark ? '#5a6a8a' : '#999', textTransform:'uppercase',
            letterSpacing:'0.08em', fontWeight:600, marginBottom:12 }}>
            Edit {editingLine.lineType === 'parts' ? 'Part' : editingLine.lineType === 'misc' ? 'Misc' : 'Labor'} Line
            {editingLine.itemID && editingLine.itemID !== '0' && (
              <div style={{ fontSize:13, color:T.text, textTransform:'none', letterSpacing:0, fontWeight:600, marginTop:5 }}>
                {editingLine.description}
              </div>
            )}
          </div>
          <textarea
            autoFocus
            value={editForm.description}
            onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
            placeholder={editingLine.lineType === 'parts' ? 'Note — prints on the receipt' : 'Description'}
            rows={editingLine.lineType === 'parts' ? 3 : 5}
            style={{ width:'100%', boxSizing:'border-box', background: isDark ? '#0f1724' : '#fff',
              border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`, borderRadius:6, padding:'8px 10px',
              color:T.text, fontSize:13, fontFamily:'Arial, sans-serif', marginBottom:10, outline:'none', resize:'vertical' }}
          />
          {editingLine.lineType === 'parts' ? (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:12 }}>
              <div>
                <div style={{ fontSize:10, color:T.textSub, marginBottom:3 }}>Quantity</div>
                <input type="number" min="1" value={editForm.qty}
                  onChange={e => setEditForm(f => ({ ...f, qty: e.target.value }))}
                  style={{ width:'100%', boxSizing:'border-box', height:30, fontSize:12, textAlign:'center',
                    background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`, borderRadius:5, color:T.text }} />
              </div>
              <div>
                {/* Read-only. Part pricing comes from the catalog; per-ticket
                    price overrides are a Lightspeed decision and nothing has
                    asked for them here. */}
                <div style={{ fontSize:10, color:T.textSub, marginBottom:3 }}>Unit price</div>
                <div style={{ height:30, display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:12, color:T.textSub, border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`,
                  borderRadius:5, background: isDark ? '#141d30' : '#f4f4ef' }}>
                  ${Number(editingLine.unitPrice || 0).toFixed(2)}
                </div>
              </div>
            </div>
          ) : editingLine.lineType === 'misc' ? (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:12 }}>
              <div>
                <div style={{ fontSize:10, color:T.textSub, marginBottom:3 }}>Quantity</div>
                <input type="number" min="1" value={editForm.qty}
                  onChange={e => setEditForm(f => ({ ...f, qty: e.target.value }))}
                  style={{ width:'100%', boxSizing:'border-box', height:30, fontSize:12, textAlign:'center',
                    background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`, borderRadius:5, color:T.text }} />
              </div>
              <div>
                <div style={{ fontSize:10, color:T.textSub, marginBottom:3 }}>Unit price</div>
                <input type="number" min="0" step="0.01" value={editForm.unitPrice}
                  onChange={e => setEditForm(f => ({ ...f, unitPrice: e.target.value }))}
                  style={{ width:'100%', boxSizing:'border-box', height:30, fontSize:12, textAlign:'center',
                    background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`, borderRadius:5, color:T.text }} />
              </div>
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:12 }}>
              <div>
                <div style={{ fontSize:10, color:T.textSub, marginBottom:3 }}>Hours</div>
                <input type="number" min="0" value={editForm.hours}
                  onChange={e => setEditForm(f => {
                    const next = { ...f, hours: e.target.value };
                    const p = recalcEditPrice(next.hours, next.minutes);
                    return p === null ? next : { ...next, unitPrice: p };
                  })}
                  style={{ width:'100%', boxSizing:'border-box', height:30, fontSize:12, textAlign:'center',
                    background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`, borderRadius:5, color:T.text }} />
              </div>
              <div>
                <div style={{ fontSize:10, color:T.textSub, marginBottom:3 }}>Minutes</div>
                <input type="number" min="0" value={editForm.minutes}
                  onChange={e => setEditForm(f => {
                    const next = { ...f, minutes: e.target.value };
                    const p = recalcEditPrice(next.hours, next.minutes);
                    return p === null ? next : { ...next, unitPrice: p };
                  })}
                  style={{ width:'100%', boxSizing:'border-box', height:30, fontSize:12, textAlign:'center',
                    background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`, borderRadius:5, color:T.text }} />
              </div>
              <div>
                <div style={{ fontSize:10, color:T.textSub, marginBottom:3 }}>Price</div>
                <input type="number" min="0" step="0.01" value={editForm.unitPrice}
                  onChange={e => { setEditPriceManual(true); setEditForm(f => ({ ...f, unitPrice: e.target.value })); }}
                  style={{ width:'100%', boxSizing:'border-box', height:30, fontSize:12, textAlign:'center',
                    background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`, borderRadius:5, color:T.text }} />
              </div>
            </div>
          )}
          {(editingLine.lineType || 'labor') === 'labor' && noRateHint}
          {editSuggestion && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10,
              marginTop:-4, marginBottom:12, padding:'8px 10px', borderRadius:6,
              background: isDark ? '#0f1e30' : '#e8f0f8', border:'1px solid #3498db55' }}>
              <span style={{ fontSize:12, color:T.text }}>{editSuggestion.label}</span>
              <button type="button"
                onClick={() => { setEditPriceManual(true); setEditForm(f => ({ ...f, unitPrice: editSuggestion.price })); }}
                style={{ height:26, padding:'0 10px', fontSize:12, borderRadius:5, cursor:'pointer',
                  background:'#3498db', color:'#fff', border:'none', fontFamily:'Arial, sans-serif', whiteSpace:'nowrap' }}>
                Use this price
              </button>
            </div>
          )}
          {editingLine.lineType !== 'parts' && <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:10, color:T.textSub, marginBottom:3 }}>Employee</div>
            <select value={editForm.employeeID}
              onChange={e => setEditForm(f => ({ ...f, employeeID: e.target.value }))}
              style={{ width:'100%', boxSizing:'border-box', height:30, fontSize:12,
                background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`,
                borderRadius:5, color:T.text, cursor:'pointer', fontFamily:'inherit', outline:'none' }}>
              <option value="" style={{ background: isDark ? '#0f1724' : '#fff', color:T.text }}>Unassigned</option>
              {employees.map(e => <option key={e.id} value={e.id} style={{ background: isDark ? '#0f1724' : '#fff', color:T.text }}>{e.name}</option>)}
            </select>
          </div>}
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:10, color:T.textSub, marginBottom:3 }}>Discount</div>
            <select value={editForm.discountID}
              onChange={e => setEditForm(f => ({ ...f, discountID: e.target.value }))}
              style={{ width:'100%', boxSizing:'border-box', height:30, fontSize:12,
                background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`,
                borderRadius:5, color:T.text, cursor:'pointer', fontFamily:'inherit', outline:'none' }}>
              <option value="" style={{ background: isDark ? '#0f1724' : '#fff', color:T.text }}>No discount</option>
              {discounts.map(d => (
                <option key={d.id} value={d.id} style={{ background: isDark ? '#0f1724' : '#fff', color:T.text }}>
                  {d.name}{d.percent ? ` (${(d.percent * 100).toFixed(d.percent * 100 % 1 ? 1 : 0)}%)` : ''}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display:'flex', gap:18, marginBottom:14 }}>
            <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:T.text, cursor:'pointer' }}>
              <input type="checkbox" checked={editForm.warranty}
                onChange={e => setEditForm(f => ({ ...f, warranty: e.target.checked }))}
                style={{ cursor:'pointer' }} />
              Warranty
            </label>
            <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:T.text, cursor:'pointer' }}>
              <input type="checkbox" checked={editForm.tax}
                onChange={e => setEditForm(f => ({ ...f, tax: e.target.checked }))}
                style={{ cursor:'pointer' }} />
              Tax
            </label>
          </div>
          {editForm.warranty && (
            <div style={{ fontSize:11, color:'#e67e22', marginBottom:12, lineHeight:1.5 }}>
              Warranty zeroes out the charge for this line.
            </div>
          )}
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <button onClick={() => setEditingLine(null)}
              style={{ height:32, padding:'0 12px', fontSize:12, borderRadius:5, cursor:'pointer',
                background:'transparent', border:`1px solid ${isDark ? '#2e3d58' : '#d0d0c8'}`,
                color:T.textSub, fontFamily:'Arial, sans-serif' }}>
              Cancel
            </button>
            <button disabled={editBusy || (editingLine.lineType !== 'parts' && (!editingLine.itemID || editingLine.itemID === '0') && !editForm.description.trim())} onClick={submitLineEdit}
              style={{ height:32, padding:'0 16px', fontSize:12, borderRadius:5, cursor:'pointer',
                background:'#3498db', color:'#fff', border:'none', fontFamily:'Arial, sans-serif',
                opacity: editBusy ? 0.7 : 1 }}>
              {editBusy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
  );

  return { addItemPanel, lineEditorModal, updateItemQuantity, deleteLineItem, openLineEditor,
    lineStock, totalsPending, employees, discounts, checkedOut };
}
